import { Input } from '@verlo/shared/components/ui/input';
import { Notice } from '@verlo/shared/components/ui/notice';
import { ALLERGENS, dishAllergens, listWords } from '@verlo/shared/lib/allergens';
import { cn } from '@verlo/shared/lib/utils';

// A table's allergies: the UK's 14 as taps, and a line for anything else the kitchen should
// know ("severe, carries an EpiPen"). Nothing is ticked by default, so a table without an
// allergy costs the server nothing. Used by the seat sheet and by the order screen, where a
// guest's allergy often turns up after they have sat down.
//
// The count under the grid is the point of tapping rather than typing: before anyone has
// ordered, the server knows how much of the menu is off for this table.

export function countClashing(menu, allergens) {
  if (!allergens?.length || !menu?.length) return { clashing: 0, total: 0 };
  const byId = Object.fromEntries(menu.map((m) => [m.id, m]));
  const live = menu.filter((m) => m.is_available !== false);
  const wanted = new Set(allergens);
  const clashing = live.filter((m) => dishAllergens(m, byId).some((k) => wanted.has(k))).length;
  return { clashing, total: live.length };
}

export default function AllergyPicker({ allergens = [], notes = '', onChange, menu }) {
  const picked = new Set(allergens);
  const toggle = (key) => {
    const next = picked.has(key)
      ? allergens.filter((k) => k !== key)
      : ALLERGENS.map((a) => a.key).filter((k) => picked.has(k) || k === key);
    onChange({ allergens: next, notes });
  };
  const { clashing, total } = countClashing(menu, allergens);

  return (
    <div className="space-y-2" data-testid="allergy-picker">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-medium">Allergies at this table</span>
        <span className="text-xs text-ink-faint">Tap every one a guest mentions</span>
      </div>
      {/* Chips that hug their words and wrap: "Crustaceans" in a quarter-width cell ran
          out of its button on the narrow seat sheet. */}
      <div className="flex flex-wrap gap-1.5">
        {ALLERGENS.map(({ key, label }) => {
          const on = picked.has(key);
          return (
            <button
              key={key}
              type="button"
              aria-pressed={on}
              onClick={() => toggle(key)}
              data-testid={`allergen-${key}`}
              className={cn(
                'h-10 whitespace-nowrap border px-3 text-[13px] transition-colors',
                on
                  ? 'border-signal-stop-line bg-signal-stop-tint font-semibold text-signal-stop'
                  : 'border-hairline bg-white text-ink hover:bg-surface-2',
              )}
            >
              {on && <span aria-hidden="true">✓ </span>}{label}
            </button>
          );
        })}
      </div>
      <Input
        placeholder="Anything else the kitchen should know"
        value={notes}
        onChange={(e) => onChange({ allergens, notes: e.target.value })}
        data-testid="allergy-notes-input"
      />
      {allergens.length > 0 && total > 0 && (
        <Notice tone="stop" word="Allergy" data-testid="allergy-count">
          <span className="font-semibold text-ink">{listWords(allergens, { capitalise: true })}.</span>{' '}
          {clashing} of {total} dishes {clashing === 1 ? 'contains' : 'contain'} {allergens.length === 1 ? 'it' : 'one of these'}. The pad will mark them.
        </Notice>
      )}
    </div>
  );
}
